import React from "react";

function polarToCartesian(
  centerX: number,
  centerY: number,
  radius: number,
  angleInDegrees: number
) {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  };
}

const SvgArc = ({
  x,
  y,
  radius,
  startAngle,
  endAngle,
  ...rest
}: {
  x: number;
  y: number;
  radius: number;
  startAngle: number;
  endAngle: number;
} & React.SVGProps<SVGPathElement>) => {
  const start = polarToCartesian(x, y, radius, endAngle);
  const end = polarToCartesian(x, y, radius, startAngle);
  const largeArcFlag =
    Math.abs(endAngle - startAngle) <= 180 || endAngle - startAngle === 0
      ? "0"
      : "1";
  const d = [
    "M",
    start.x,
    start.y,
    "A",
    radius,
    radius,
    0,
    largeArcFlag,
    0,
    end.x,
    end.y,
  ].join(" ");

  return <path stroke="black" strokeWidth="1" d={d} {...rest} />;
};

const clamp = (num: number, min: number, max: number) =>
  Math.min(Math.max(num, min), max);

const getColor = (
  value: number,
  min: number,
  max: number,
  saturation = 100,
  lightness = 50
) => {
  const clamped = clamp(value, min, max);
  const percentage = (clamped - min) / (max - min);
  const hue = (percentage * 120).toString(10);
  return `hsl(${hue},${saturation.toString(10)}%,${lightness.toString(10)}%)`;
};

// const getCostStyles = (price: number, costPrice: number) => {
//   const margin = (1 / costPrice) * (price - costPrice);
//   return {
//     color: getColor(margin, 0, 1, 80, 20),
//     backgroundColor: getColor(margin, 0, 1, 80, 80),
//     fontWeight: margin < 0 ? "bold" : "normal",
//   };
// };

export function Donut({
  value,
  max,
  min = 0,
  stroke = 16,
  radius = 100,
  capStyle = "round",
}: {
  value: number;
  max: number;
  min: number;
  stroke: number;
  radius: number;
  capStyle: "round" | "butt";
}) {
  const viewBox = 2 * radius + stroke;
  const circ = 2 * Math.PI * radius;
  // because SVG stroke using round ends effectively adds half stroke width to start and end of a line
  // we can use this to adjust our angles so it looks right
  const capDegrees = (360 / circ) * (stroke / 2);

  const range = max - min;

  const oneDegree = 360 / range;
  const adjustedValue = value - min;

  const degrees = Math.max(0, Math.min(360, adjustedValue * oneDegree));

  //in this case we use half of the value so there is still some overlap
  const startAngle = capStyle === "round" ? capDegrees / 2 : 0;
  const endAngle =
    capStyle === "round"
      ? degrees - capDegrees / 2
      : //if we ever get to 360 the line will be 0 length
        Math.min(359.99, degrees);

  return (
    <div
      style={{
        width: 2 * radius,
        position: "relative",
      }}
    >
      <div
        style={{
          position: "absolute",
          left: radius / 2,
          top: radius / 2,
          width: radius,
          height: radius,
          lineHeight: `${radius}px`,
          textAlign: "center",
          fontSize: "2em",
          fontWeight: "bold",
        }}
      >
        {value}
      </div>

      <svg
        viewBox={`0 0 ${viewBox} ${viewBox}`}
        style={{ width: "100%", display: "block" }}
      >
        {/*<defs>*/}
        {/*  <marker*/}
        {/*    id="InverseSemicircleEnd"*/}
        {/*    viewBox="0 0 5 10"*/}
        {/*    refX="5"*/}
        {/*    refY="5"*/}
        {/*    markerUnits="strokeWidth"*/}
        {/*    markerWidth="0.5"*/}
        {/*    markerHeight="1"*/}
        {/*    orient="auto"*/}
        {/*    fill="red"*/}
        {/*  >*/}
        {/*    <path*/}
        {/*      fill="none"*/}
        {/*      stroke={getColor(value, min, max, 80, 40)}*/}
        {/*      strokeWidth={0.25}*/}
        {/*      strokeDasharray="none"*/}
        {/*      d="M 9.641304,-4.9455009 A 4.695652,4.695652 0 0 1 7.2934779,-0.87894689 4.695652,4.695652 0 0 1 2.5978259,-0.878947 4.695652,4.695652 0 0 1 0.25,-4.9455009"*/}
        {/*      transform="rotate(90)"*/}
        {/*    />*/}
        {/*  </marker>*/}
        {/*</defs>*/}
        <SvgArc
          x={viewBox / 2}
          y={viewBox / 2}
          radius={radius}
          startAngle={0}
          endAngle={359.99}
          fill="none"
          stroke="#DDDDDD"
          strokeWidth={stroke}
        />
        <SvgArc
          x={viewBox / 2}
          y={viewBox / 2}
          radius={radius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill="none"
          stroke={getColor(value, min, max, 80, 50)}
          strokeWidth={stroke}
          strokeLinecap={capStyle}
          // marker-start="url(#InverseSemicircleEnd)"
        />
      </svg>
    </div>
  );
}
